import type {
  EquipmentDomain,
  EquipmentKind,
  EquipmentLevel,
  EquipmentLifecycleStatus,
  EquipmentTechnology,
} from "../../types/equipmentLibrary";
import {
  EQUIPMENT_SOURCE_BY_ID,
  EQUIPMENT_SOURCE_LICENSE_MANIFEST,
} from "./sourceLicenseManifest";
import type {
  CatalogStatus,
  CommercialMaturity,
  CuratedOemSeedRecord,
  EvidenceTier,
} from "./seedSchema";

interface SeedItem {
  family: string;
  catalogStatus?: CatalogStatus;
  commercialMaturity?: CommercialMaturity;
  lifecycleStatus?: EquipmentLifecycleStatus;
  regions?: readonly string[];
  frequencyHz?: readonly (50 | 60)[];
  dataGaps?: readonly string[];
}

interface SeedGroup {
  manufacturer: string;
  sourceIds: readonly string[];
  evidenceTier?: EvidenceTier;
  level: EquipmentLevel;
  domain: EquipmentDomain;
  kind: EquipmentKind;
  technologies: readonly EquipmentTechnology[];
  energySources: readonly string[];
  applications: readonly string[];
  catalogStatus?: CatalogStatus;
  commercialMaturity?: CommercialMaturity;
  lifecycleStatus?: EquipmentLifecycleStatus;
  regions?: readonly string[];
  frequencyHz?: readonly (50 | 60)[];
  items: readonly (string | SeedItem)[];
}

const slugify = (value: string): string =>
  value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/\+/g, " plus ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const toItem = (item: string | SeedItem): SeedItem =>
  typeof item === "string" ? { family: item } : item;

const buildGaps = (
  item: SeedItem,
  group: SeedGroup,
): readonly string[] => {
  const regions = item.regions ?? group.regions ?? [];
  const frequencyHz = item.frequencyHz ?? group.frequencyHz ?? [];
  return [
    "Identity record only: no normalized numeric performance observations have been admitted from the cited source.",
    ...(regions.length === 0
      ? ["Regional commercial availability requires current OEM confirmation."]
      : []),
    ...(frequencyHz.length === 0
      ? [
          "Grid-frequency compatibility is not applicable or has not been normalized from the cited source.",
        ]
      : []),
    "Project-specific configuration, price, lead time, warranty, and emissions guarantees require a current OEM proposal.",
    ...(item.dataGaps ?? []),
  ];
};

const buildGroup = (group: SeedGroup): readonly CuratedOemSeedRecord[] => {
  const sourceUrls = group.sourceIds.map((sourceId) => {
    const source = EQUIPMENT_SOURCE_BY_ID.get(sourceId);
    if (!source) {
      throw new Error(`Unknown equipment evidence source: ${sourceId}`);
    }
    return source.url;
  });

  return group.items.map((rawItem) => {
    const item = toItem(rawItem);
    return {
      id: `oem-${slugify(group.manufacturer)}-${slugify(item.family)}`,
      manufacturer: group.manufacturer,
      family: item.family,
      classification: {
        level: group.level,
        domain: group.domain,
        kind: group.kind,
        technologies: group.technologies,
        energySources: group.energySources,
        applications: group.applications,
      },
      catalogStatus: item.catalogStatus ?? group.catalogStatus ?? "active",
      commercialMaturity:
        item.commercialMaturity ??
        group.commercialMaturity ??
        "commercially-available",
      lifecycleStatus:
        item.lifecycleStatus ??
        group.lifecycleStatus ??
        "commercial-current",
      evidenceTier: group.evidenceTier ?? "tier-1-oem-primary",
      sourceIds: group.sourceIds,
      sourceUrls,
      regions: item.regions ?? group.regions ?? [],
      frequencyHz: item.frequencyHz ?? group.frequencyHz ?? [],
      dataGaps: buildGaps(item, group),
    } satisfies CuratedOemSeedRecord;
  });
};

const GROUPS: readonly SeedGroup[] = [
  {
    manufacturer: "GE Vernova",
    sourceIds: ["ge-vernova-gas-catalog-2025"],
    level: "component",
    domain: "energy-conversion",
    kind: "gas-turbine",
    technologies: [
      "natural-gas-simple-cycle",
      "natural-gas-combined-cycle",
    ],
    energySources: ["natural gas", "OEM-qualified alternative fuels"],
    applications: ["simple cycle", "combined cycle", "CHP", "fast power"],
    items: [
      "9HA.01",
      "9HA.02",
      "7HA.01",
      "7HA.02",
      "7HA.03",
      "9F.05",
      "7F.05",
      "7F.04",
      "7E.03",
      "6F.03",
      "6B.03",
      "LM2500",
      "LM2500+G4",
      "LM2500XPRESS",
      "LM6000",
      "LM6000PF+",
      "LM6000VELOX",
      "LMS100",
      "TM2500",
    ],
  },
  {
    manufacturer: "Siemens Energy",
    sourceIds: ["siemens-energy-gas-turbines"],
    level: "component",
    domain: "energy-conversion",
    kind: "gas-turbine",
    technologies: [
      "natural-gas-simple-cycle",
      "natural-gas-combined-cycle",
    ],
    energySources: ["natural gas", "OEM-qualified alternative fuels"],
    applications: [
      "simple cycle",
      "combined cycle",
      "CHP",
      "mechanical drive",
    ],
    items: [
      "SGT5-9000HL",
      "SGT6-9000HL",
      "SGT5-8000H",
      "SGT6-8000H",
      "SGT5-4000F",
      "SGT6-5000F",
      "SGT5-2000E",
      "SGT6-2000E",
      "SGT-800",
      "SGT-750",
      "SGT-700",
      "SGT-600",
      "SGT-A35",
      "SGT-400",
      "SGT-300",
      "SGT-100",
      "SGT-50",
      "SGT-A05",
    ],
  },
  {
    manufacturer: "Mitsubishi Power",
    sourceIds: ["mitsubishi-power-gas-lineup"],
    level: "component",
    domain: "energy-conversion",
    kind: "gas-turbine",
    technologies: [
      "natural-gas-simple-cycle",
      "natural-gas-combined-cycle",
    ],
    energySources: ["natural gas", "OEM-qualified alternative fuels"],
    applications: ["simple cycle", "combined cycle", "CHP"],
    regions: ["global"],
    items: [
      { family: "H-25 Series", frequencyHz: [50, 60] },
      { family: "H-100 Series", frequencyHz: [50, 60] },
      { family: "M501G/GAC Series", frequencyHz: [60] },
      { family: "M501J/JAC Series", frequencyHz: [60] },
      { family: "M701D Series", frequencyHz: [50] },
      { family: "M701F Series", frequencyHz: [50] },
      { family: "M701J/JAC Series", frequencyHz: [50] },
    ],
  },
  {
    manufacturer: "Mitsubishi Power Aero",
    sourceIds: ["mitsubishi-power-aeroderivative-lineup"],
    level: "packaged-system",
    domain: "energy-conversion",
    kind: "generation-package",
    technologies: ["natural-gas-simple-cycle"],
    energySources: ["natural gas", "OEM-qualified alternative fuels"],
    applications: ["mobile power", "fast power", "simple cycle"],
    items: ["FT8 MOBILEPAC", "FT8 SWIFTPAC", "FT4000 SWIFTPAC"],
  },
  {
    manufacturer: "Solar Turbines",
    sourceIds: ["solar-turbines-ratings"],
    level: "component",
    domain: "energy-conversion",
    kind: "gas-turbine",
    technologies: ["natural-gas-simple-cycle", "chp"],
    energySources: ["natural gas", "OEM-qualified alternative fuels"],
    applications: ["power generation", "CHP", "mechanical drive"],
    items: [
      "Saturn 20",
      "Centaur 40",
      "Centaur 50",
      "Mercury 50",
      "Taurus 60",
      "Taurus 70",
      "Mars 90",
      "Mars 100",
      "Titan 130",
      "Titan 250",
      "Titan 350 (35 MW configuration)",
      "Titan 350 (39 MW configuration)",
    ],
  },
  {
    manufacturer: "Siemens Energy",
    sourceIds: [
      "siemens-energy-industrial-steam",
      "siemens-energy-utility-steam",
    ],
    level: "component",
    domain: "energy-conversion",
    kind: "steam-turbine",
    technologies: [
      "natural-gas-combined-cycle",
      "coal-steam",
      "biomass-steam",
      "waste-to-energy",
      "chp",
    ],
    energySources: ["steam from compatible thermal source"],
    applications: [
      "condensing",
      "back-pressure",
      "extraction",
      "combined cycle",
      "industrial power",
    ],
    items: [
      "SST-200",
      "SST-300",
      "SST-400",
      "SST-500",
      "SST-600",
      "SST-700",
      "SST-800",
      "SST-900",
      "SST-3000",
      "SST-4000",
      "SST-5000",
      { family: "SST-6000", frequencyHz: [50, 60] },
      "SST-8000",
      "SST-9000",
    ],
  },
  {
    manufacturer: "GE Vernova",
    sourceIds: ["ge-vernova-stf-a200", "ge-vernova-stf-d600"],
    level: "component",
    domain: "energy-conversion",
    kind: "steam-turbine",
    technologies: [
      "natural-gas-combined-cycle",
      "coal-steam",
      "biomass-steam",
      "waste-to-energy",
      "chp",
    ],
    energySources: ["steam from compatible thermal source"],
    applications: ["combined cycle", "steam power", "industrial power"],
    items: [
      "STF-A100",
      "STF-A200",
      "STF-D100",
      "STF-D200",
      "STF-D600",
      "STF-D650",
    ],
  },
  {
    manufacturer: "Wärtsilä",
    sourceIds: ["wartsila-engine-products"],
    level: "component",
    domain: "energy-conversion",
    kind: "reciprocating-engine",
    technologies: [
      "reciprocating-gas",
      "reciprocating-liquid-fuel",
      "dual-fuel-generation",
      "chp",
    ],
    energySources: [
      "natural gas",
      "liquid fuels",
      "OEM-qualified alternative fuels",
    ],
    applications: ["balancing power", "baseload", "CHP", "microgrid"],
    items: [
      "46TS-DF",
      "46TS-SG",
      "31SG",
      "31SG Power Optimised",
      "31SG Efficiency Optimised",
      "31DF",
      "34SG",
      "34SG Power Optimised",
      "34DF",
      "34SG-LPG",
      "50SG",
      "50DF",
      "32 Diesel",
      "50 Diesel",
    ],
  },
  {
    manufacturer: "INNIO Jenbacher",
    sourceIds: ["innio-jenbacher-portfolio", "innio-j920-flextra"],
    level: "component",
    domain: "energy-conversion",
    kind: "reciprocating-engine",
    technologies: ["reciprocating-gas", "chp"],
    energySources: ["natural gas", "biogas", "OEM-qualified specialty gases"],
    applications: ["power generation", "CHP", "microgrid"],
    items: [
      "J208",
      "J312",
      "J316",
      "J320",
      "J412",
      "J416",
      "J420",
      "J612",
      "J616",
      "J620",
      "J624",
      "J920 FleXtra",
    ],
  },
  {
    manufacturer: "Caterpillar",
    sourceIds: ["cat-gas-generator-sets", "cat-cg260-16"],
    level: "packaged-system",
    domain: "energy-conversion",
    kind: "generation-package",
    technologies: ["reciprocating-gas", "chp"],
    energySources: ["natural gas", "biogas", "OEM-qualified gas blends"],
    applications: ["distributed generation", "CHP", "microgrid"],
    items: [
      { family: "CG132B-8", frequencyHz: [50, 60] },
      { family: "CG132B-12", frequencyHz: [50, 60] },
      { family: "CG132B-16", frequencyHz: [50, 60] },
      "CG170B-12",
      "CG170B-16",
      "CG170B-20",
      "CG260-12",
      { family: "CG260-16", frequencyHz: [50, 60] },
      "G3516",
      "G3520H",
    ],
  },
  {
    manufacturer: "Vestas",
    sourceIds: ["vestas-product-portfolio", "vestas-v236"],
    level: "packaged-system",
    domain: "renewable-generation",
    kind: "wind-turbine",
    technologies: ["wind-onshore", "wind-offshore"],
    energySources: ["wind"],
    applications: ["onshore wind", "offshore wind"],
    items: [
      "V150-4.5 MW",
      "V162-7.2 MW",
      "V163-4.5 MW",
      "V172-7.2 MW",
      "V174-9.5 MW",
      "V236-15.0 MW",
    ],
  },
  {
    manufacturer: "Siemens Gamesa",
    sourceIds: ["siemens-gamesa-wind-portfolio"],
    level: "packaged-system",
    domain: "renewable-generation",
    kind: "wind-turbine",
    technologies: ["wind-onshore", "wind-offshore"],
    energySources: ["wind"],
    applications: ["onshore wind", "offshore wind"],
    items: [
      "SG 5.0-145",
      "SG 6.6-155",
      "SG 7.0-170",
      "SG 11.0-200 DD",
      "SG 14-222 DD",
      "SG 14-236 DD",
    ],
  },
  {
    manufacturer: "Nordex Group",
    sourceIds: ["nordex-product-portfolio"],
    level: "packaged-system",
    domain: "renewable-generation",
    kind: "wind-turbine",
    technologies: ["wind-onshore"],
    energySources: ["wind"],
    applications: ["onshore wind"],
    items: ["N149/5.X", "N163/5.X", "N163/6.X", "N169/5.X", "N175/6.X"],
  },
  {
    manufacturer: "GE Vernova",
    sourceIds: ["ge-vernova-wind-portfolio"],
    level: "packaged-system",
    domain: "renewable-generation",
    kind: "wind-turbine",
    technologies: ["wind-onshore", "wind-offshore"],
    energySources: ["wind"],
    applications: ["onshore wind", "offshore wind"],
    items: ["Cypress 6.1-158", "Sierra 3.4-140", "Haliade-X 14.7-220"],
  },
  {
    manufacturer: "First Solar",
    sourceIds: ["first-solar-products"],
    level: "component",
    domain: "renewable-generation",
    kind: "solar-module",
    technologies: ["solar-pv"],
    energySources: ["solar"],
    applications: ["utility-scale photovoltaic generation"],
    items: [
      "Series 6 Plus",
      "Series 6 Plus Bifacial",
      "Series 7 FT1",
      "Series 7 TR1",
    ],
  },
  {
    manufacturer: "Maxeon Solar Technologies",
    sourceIds: ["maxeon-technical-documents"],
    level: "component",
    domain: "renewable-generation",
    kind: "solar-module",
    technologies: ["solar-pv"],
    energySources: ["solar"],
    applications: ["commercial photovoltaic generation", "utility PV"],
    items: [
      "Maxeon 3",
      "Maxeon 6",
      "Maxeon 7",
      "Performance 6 COM",
      "Performance 6 UPP",
      "Performance 7",
    ],
  },
  {
    manufacturer: "Sungrow",
    sourceIds: ["sungrow-sg350hx"],
    level: "component",
    domain: "renewable-generation",
    kind: "solar-inverter",
    technologies: ["solar-pv"],
    energySources: ["solar"],
    applications: ["utility-scale photovoltaic generation"],
    items: ["SG350HX"],
  },
  {
    manufacturer: "SMA Solar Technology",
    sourceIds: ["sma-sunny-central-up"],
    level: "packaged-system",
    domain: "renewable-generation",
    kind: "solar-inverter",
    technologies: ["solar-pv"],
    energySources: ["solar"],
    applications: ["utility-scale photovoltaic generation"],
    items: ["Sunny Central UP"],
  },
  {
    manufacturer: "Voith",
    sourceIds: ["voith-hydro-turbines"],
    level: "component",
    domain: "renewable-generation",
    kind: "hydro-turbine",
    technologies: [
      "hydro-francis",
      "hydro-kaplan",
      "hydro-pelton",
      "hydro-bulb",
      "pumped-hydro",
    ],
    energySources: ["hydraulic head and flow"],
    applications: ["hydroelectric generation", "pumped storage"],
    regions: ["global"],
    items: [
      "Francis Turbine",
      "Kaplan Turbine",
      "Pelton Turbine",
      "Bulb Turbine",
      "Pit Turbine",
      "S-Turbine",
      {
        family: "Reversible Pump-Turbine",
        dataGaps: [
          "Pumping and generating maps must be represented as separate attributed observations.",
        ],
      },
    ],
  },
  {
    manufacturer: "Voith",
    sourceIds: ["voith-small-hydro"],
    level: "packaged-system",
    domain: "renewable-generation",
    kind: "generation-package",
    technologies: ["hydro-francis", "hydro-kaplan", "hydro-pelton"],
    energySources: ["hydraulic head and flow"],
    applications: ["small hydropower"],
    items: [
      "Small Hydro Francis Package",
      "Small Hydro Kaplan Package",
      "Small Hydro Pelton Package",
      "StreamDiver",
      "StreamDiver eQ",
    ],
  },
  {
    manufacturer: "Voith",
    sourceIds: ["voith-hycomplete"],
    level: "packaged-system",
    domain: "renewable-generation",
    kind: "generation-package",
    technologies: ["hydro-francis", "hydro-kaplan", "hydro-pelton"],
    energySources: ["hydraulic head and flow"],
    applications: ["small hydropower", "standardized hydropower plant"],
    items: [
      "HyComplete",
      "M-Line Francis",
      "M-Line Kaplan",
      "M-Line Pelton",
    ],
  },
  {
    manufacturer: "Valmet",
    sourceIds: ["valmet-cfb-boilers"],
    level: "component",
    domain: "thermal-balance-of-plant",
    kind: "boiler",
    technologies: ["biomass-steam", "waste-to-energy", "coal-steam", "chp"],
    energySources: ["biomass", "waste-derived fuels", "solid fuels"],
    applications: ["steam generation", "CHP", "waste-to-energy"],
    items: ["Circulating Fluidized Bed Boiler", "CYMIC CFB Boiler"],
  },
  {
    manufacturer: "Valmet",
    sourceIds: ["valmet-bfb-boilers"],
    level: "component",
    domain: "thermal-balance-of-plant",
    kind: "boiler",
    technologies: ["biomass-steam", "waste-to-energy", "chp"],
    energySources: ["biomass", "waste-derived fuels", "solid fuels"],
    applications: ["steam generation", "CHP", "waste-to-energy"],
    items: ["Bubbling Fluidized Bed Boiler", "HYBEX BFB Boiler"],
  },
  {
    manufacturer: "GE Vernova",
    sourceIds: ["ge-vernova-hrsg"],
    level: "component",
    domain: "thermal-balance-of-plant",
    kind: "heat-recovery-steam-generator",
    technologies: ["natural-gas-combined-cycle", "chp"],
    energySources: ["gas-turbine exhaust heat"],
    applications: ["combined cycle", "CHP"],
    items: [
      "Horizontal Drum HRSG",
      "Horizontal Once-Through HRSG",
      "Vertical Drum HRSG",
    ],
  },
  {
    manufacturer: "John Cockerill",
    sourceIds: ["john-cockerill-hrsg", "john-cockerill-boilers"],
    level: "component",
    domain: "thermal-balance-of-plant",
    kind: "heat-recovery-steam-generator",
    technologies: [
      "natural-gas-combined-cycle",
      "biomass-steam",
      "waste-to-energy",
      "chp",
    ],
    energySources: ["exhaust heat", "biomass", "compatible thermal fuels"],
    applications: ["combined cycle", "industrial steam", "CHP"],
    items: [
      "Horizontal HRSG",
      "Vertical HRSG",
      "H2-Ready HRSG",
      "Offshore HRSG",
      "Small Modular Boiler",
      "Once Through Boiler",
      "Denapak Boiler",
      "Denarad Boiler",
      "Biomass Steam Boiler",
    ],
  },
  {
    manufacturer: "Turboden",
    sourceIds: ["turboden-orc"],
    level: "packaged-system",
    domain: "energy-conversion",
    kind: "organic-rankine-cycle",
    technologies: ["geothermal-binary"],
    energySources: [
      "geothermal heat",
      "biomass heat",
      "industrial waste heat",
      "solar heat",
    ],
    applications: [
      "geothermal generation",
      "biomass generation",
      "waste heat recovery",
    ],
    items: [
      "ORC System",
      "Biomass ORC",
      "Geothermal ORC",
      "Waste Heat Recovery ORC",
      "Gas Expansion ORC",
    ],
  },
  {
    manufacturer: "Aalborg CSP",
    sourceIds: ["aalborg-csp-receiver"],
    level: "component",
    domain: "renewable-generation",
    kind: "solar-thermal-receiver",
    technologies: ["solar-csp"],
    energySources: ["concentrated solar heat"],
    applications: ["central receiver CSP", "direct steam generation"],
    items: ["Direct Steam Solar Tower Receiver"],
  },
  {
    manufacturer: "Rioglass",
    sourceIds: ["rioglass-csp-mirrors"],
    level: "component",
    domain: "renewable-generation",
    kind: "other",
    technologies: ["solar-csp"],
    energySources: ["solar"],
    applications: ["parabolic trough CSP", "central receiver CSP"],
    items: ["RP-2 Mirror", "RP-3 Mirror", "RP-4 Mirror"],
  },
  {
    manufacturer: "Westinghouse Electric Company",
    sourceIds: ["westinghouse-ap1000"],
    level: "plant-configuration",
    domain: "nuclear-generation",
    kind: "nuclear-plant",
    technologies: ["nuclear-pwr"],
    energySources: ["nuclear fission"],
    applications: ["grid-scale nuclear generation"],
    commercialMaturity: "commercial-operating",
    lifecycleStatus: "commercial-current",
    items: ["AP1000"],
  },
  {
    manufacturer: "GE Hitachi Nuclear Energy",
    sourceIds: ["ge-hitachi-bwrx300"],
    level: "packaged-system",
    domain: "nuclear-generation",
    kind: "nuclear-reactor-module",
    technologies: ["nuclear-bwr"],
    energySources: ["nuclear fission"],
    applications: ["small modular reactor power plant"],
    catalogStatus: "precommercial",
    commercialMaturity: "precommercial",
    lifecycleStatus: "under-construction",
    items: ["BWRX-300"],
  },
  {
    manufacturer: "NuScale Power",
    sourceIds: ["nuscale-npm"],
    level: "packaged-system",
    domain: "nuclear-generation",
    kind: "nuclear-reactor-module",
    technologies: ["nuclear-pwr"],
    energySources: ["nuclear fission"],
    applications: ["small modular reactor power plant"],
    catalogStatus: "precommercial",
    commercialMaturity: "precommercial",
    lifecycleStatus: "licensed-precommercial",
    items: ["NuScale Power Module"],
  },
  {
    manufacturer: "NuScale Power",
    sourceIds: ["nuscale-npm"],
    level: "plant-configuration",
    domain: "nuclear-generation",
    kind: "nuclear-plant",
    technologies: ["nuclear-pwr"],
    energySources: ["nuclear fission"],
    applications: ["small modular reactor power plant"],
    catalogStatus: "precommercial",
    commercialMaturity: "precommercial",
    lifecycleStatus: "announced",
    items: ["VOYGR-4", "VOYGR-6", "VOYGR-12"],
  },
  {
    manufacturer: "Bloom Energy",
    sourceIds: ["bloom-energy-server"],
    level: "packaged-system",
    domain: "energy-conversion",
    kind: "fuel-cell",
    technologies: ["fuel-cell-generation", "chp"],
    energySources: ["natural gas", "biogas", "hydrogen"],
    applications: ["distributed generation", "microgrid", "CHP"],
    items: ["Energy Server 6.5"],
  },
  {
    manufacturer: "Doosan Fuel Cell",
    sourceIds: ["doosan-fuel-cell-products"],
    level: "packaged-system",
    domain: "energy-conversion",
    kind: "fuel-cell",
    technologies: ["fuel-cell-generation", "chp"],
    energySources: ["natural gas", "hydrogen"],
    applications: ["distributed generation", "CHP"],
    items: [
      "PureCell M400 NG",
      "PureCell M400 Hydrogen",
      "PureCell M400 LPG/NG Dual",
      "PureCell M500 Hydrogen",
      "PureCell Tri-Gen",
    ],
  },
  {
    manufacturer: "Tesla",
    sourceIds: ["tesla-megapack"],
    level: "packaged-system",
    domain: "energy-storage",
    kind: "battery-container",
    technologies: ["battery-storage"],
    energySources: ["electricity"],
    applications: ["grid storage", "renewable firming", "capacity"],
    items: ["Megapack"],
  },
  {
    manufacturer: "Wärtsilä",
    sourceIds: ["wartsila-quantum3"],
    level: "packaged-system",
    domain: "energy-storage",
    kind: "battery-container",
    technologies: ["battery-storage"],
    energySources: ["electricity"],
    applications: ["grid storage", "renewable firming"],
    items: ["Quantum3"],
  },
  {
    manufacturer: "Fluence",
    sourceIds: ["fluence-gridstack-pro"],
    level: "packaged-system",
    domain: "energy-storage",
    kind: "battery-container",
    technologies: ["battery-storage"],
    energySources: ["electricity"],
    applications: ["grid storage", "renewable firming"],
    regions: ["global"],
    items: ["Gridstack Pro"],
  },
  {
    manufacturer: "Sungrow",
    sourceIds: ["sungrow-powertitan"],
    level: "packaged-system",
    domain: "energy-storage",
    kind: "battery-container",
    technologies: ["battery-storage"],
    energySources: ["electricity"],
    applications: ["grid storage", "renewable firming"],
    items: ["PowerTitan 2.0"],
  },
  {
    manufacturer: "Orbital Marine Power",
    sourceIds: ["orbital-o2"],
    level: "packaged-system",
    domain: "renewable-generation",
    kind: "marine-energy-converter",
    technologies: ["marine-tidal"],
    energySources: ["tidal stream"],
    applications: ["tidal power generation"],
    commercialMaturity: "demonstration",
    lifecycleStatus: "demonstrator",
    items: ["O2 Tidal Turbine"],
  },
  {
    manufacturer: "CorPower Ocean",
    sourceIds: ["corpower-c4"],
    level: "packaged-system",
    domain: "renewable-generation",
    kind: "marine-energy-converter",
    technologies: ["marine-wave"],
    energySources: ["ocean waves"],
    applications: ["wave power generation"],
    commercialMaturity: "demonstration",
    lifecycleStatus: "demonstrator",
    items: ["C4 Wave Energy Converter"],
  },
  {
    manufacturer: "Sulzer",
    sourceIds: ["sulzer-hpt"],
    level: "component",
    domain: "fluid-balance-of-plant",
    kind: "pump",
    technologies: [],
    energySources: [],
    applications: ["boiler feedwater", "high-pressure process service"],
    items: ["HPT High-Pressure Barrel Casing Pump"],
  },
  {
    manufacturer: "SPX Cooling Technologies",
    sourceIds: ["marley-nc"],
    level: "packaged-system",
    domain: "thermal-balance-of-plant",
    kind: "cooling-tower",
    technologies: [],
    energySources: [],
    applications: ["heat rejection", "power plant cooling"],
    items: ["Marley NC Cooling Tower"],
  },
  {
    manufacturer: "Alfa Laval",
    sourceIds: ["alfa-laval-alfacond"],
    level: "component",
    domain: "thermal-balance-of-plant",
    kind: "condenser",
    technologies: [],
    energySources: [],
    applications: ["steam condensation", "heat recovery"],
    items: ["AlfaCond Plate Condenser"],
  },
  {
    manufacturer: "GE Vernova",
    sourceIds: ["ge-vernova-hydrogen-generators"],
    level: "component",
    domain: "electrical-balance-of-plant",
    kind: "synchronous-generator",
    technologies: [],
    energySources: [],
    applications: ["gas power", "steam power", "combined cycle"],
    frequencyHz: [50, 60],
    items: ["Gen-H Hydrogen-Cooled Generator"],
  },
  {
    manufacturer: "Hitachi Energy",
    sourceIds: ["hitachi-energy-gsu"],
    level: "component",
    domain: "electrical-balance-of-plant",
    kind: "transformer",
    technologies: [],
    energySources: [],
    applications: ["generator step-up", "grid interconnection"],
    items: ["Generator Step-Up Transformer"],
  },
  {
    manufacturer: "Atlas Copco",
    sourceIds: ["atlas-copco-gt"],
    level: "packaged-system",
    domain: "fluid-balance-of-plant",
    kind: "compressor",
    technologies: [],
    energySources: [],
    applications: ["process gas compression", "industrial gas service"],
    items: ["GT Series Centrifugal Compressor"],
  },
  {
    manufacturer: "Baker Hughes",
    sourceIds: ["baker-hughes-compressors"],
    level: "component",
    domain: "fluid-balance-of-plant",
    kind: "compressor",
    technologies: [],
    energySources: [],
    applications: ["process gas compression", "pipeline", "LNG"],
    items: [
      "MCL Centrifugal Compressor",
      "BCL Centrifugal Compressor",
      "PCL Centrifugal Compressor",
      "ICL Integrated Compressor",
      "HPRC High-Pressure-Ratio Compressor",
      "AN Axial Compressor",
    ],
  },
  {
    manufacturer: "John Cockerill Hamon",
    sourceIds: ["john-cockerill-cooling"],
    level: "packaged-system",
    domain: "thermal-balance-of-plant",
    kind: "cooling-tower",
    technologies: [],
    energySources: [],
    applications: ["heat rejection", "power plant cooling"],
    regions: [
      "Europe",
      "North America",
      "South America",
      "Middle East",
      "Asia",
    ],
    items: ["Wet Cooling Tower", "Wet-Dry Cooling Tower"],
  },
  {
    manufacturer: "John Cockerill Hamon",
    sourceIds: ["john-cockerill-cooling"],
    level: "packaged-system",
    domain: "thermal-balance-of-plant",
    kind: "condenser",
    technologies: [],
    energySources: [],
    applications: ["air-cooled steam condensation", "power plant cooling"],
    regions: [
      "Europe",
      "North America",
      "South America",
      "Middle East",
      "Asia",
    ],
    items: ["Air-Cooled Condenser"],
  },
] as const;

export const CURATED_OEM_SEED_SCHEMA_VERSION = 1 as const;
export const CURATED_OEM_SEED_COMPILED_AT = "2026-07-23T00:00:00.000Z";

/**
 * Curated product identities only. Numerical performance facts deliberately
 * remain absent until ingested as cited canonical EquipmentObservations.
 */
export const CURATED_OEM_SEED: readonly CuratedOemSeedRecord[] =
  GROUPS.flatMap(buildGroup);

export const CURATED_OEM_SEED_SOURCES =
  EQUIPMENT_SOURCE_LICENSE_MANIFEST;
